import { assert } from "@msys/common";
import { Autocomplete, TextField, TextFieldProps } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { throttle } from "lodash-es";
import React from "react";
import { useGoogleMapsApi } from "../google-maps/useGoogleMapsApi.js";

export type PlaceWithAddressComponentsAndLocation = PlaceWithAddressComponents &
  PlaceWithLocation;

interface GoogleMapsSuggestionOption {
  text: string;
  place: PlaceWithAddressComponentsAndLocation;
}
interface PassedOption<Value> {
  label: string;
  value: Value;
}

interface Props<
  Value,
  IPassedOption extends PassedOption<Value> = PassedOption<Value>,
> {
  id?: string;
  autoFocus?: boolean;
  required?: boolean;
  disabled?: boolean;
  label: string;
  initialValue?: string | null;
  onChange: (value: IPassedOption | GoogleMapsSuggestionOption | null) => void;
  /** Called with the text as the user types it (not when a suggestion is picked). */
  onInputChange?: (text: string) => void;
  error?: String;
  variant?: TextFieldProps["variant"];
  setInputValueRef?: React.MutableRefObject<
    ((string: string) => void) | undefined
  >;
  options?: IPassedOption[];
  /**
   * Keeps the input text under the caller's control after a suggestion is
   * picked (set it through `setInputValueRef`) instead of replacing it with
   * the suggestion's full text.
   */
  keepTextOnPick?: boolean;
}

export const GoogleAddressSearch = <
  Value,
  IPassedOption extends PassedOption<Value> = PassedOption<Value>,
>({
  id,
  setInputValueRef,
  autoFocus = false,
  disabled,
  required,
  label,
  initialValue,
  onChange,
  onInputChange,
  error,
  variant,
  options: passedOptions = [],
  keepTextOnPick = false,
}: Props<Value, IPassedOption>) => {
  const { t } = useTranslate(["Global"]);

  const { inputValue, setInputValue, setHasChanged } = useInputValue(
    initialValue ?? "",
    setInputValueRef
  );
  const { suggestions } = useGoogleMapsAutocomplete(inputValue);

  return (
    <Autocomplete<GoogleMapSuggestion | IPassedOption, false, boolean, true>
      id={id}
      autoComplete
      autoHighlight
      openOnFocus
      selectOnFocus
      freeSolo
      disabled={disabled}
      options={inputValue ? suggestions : passedOptions}
      filterOptions={option => option}
      getOptionLabel={option => {
        if (typeof option === "string") return option;
        return option.label;
      }}
      noOptionsText={t("No existing entry", { ns: "Global" })}
      value={inputValue}
      inputValue={keepTextOnPick ? inputValue : undefined}
      onChange={async (event, value, reason) => {
        event.stopPropagation();
        if (value === null) return;
        if (typeof value === "string") return;
        setHasChanged();

        if ("value" in value) {
          onChange(value);
        } else {
          onChange(
            value ? { text: value.label, place: await value.getPlace() } : null
          );
        }
      }}
      renderInput={params => {
        return (
          <TextField
            {...params}
            label={label}
            variant={variant}
            autoFocus={autoFocus}
            required={required}
            error={Boolean(error)}
            helperText={error}
          />
        );
      }}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "clear") {
          setInputValue("");
          onChange(null);
        }
        if (reason === "input") {
          setInputValue(newInputValue);
          onInputChange?.(newInputValue);
        }
      }}
    />
  );
};

function useInputValue(
  passedInputValue: string,
  setInputValueRef?: React.MutableRefObject<
    ((string: string) => void) | undefined
  >
) {
  const hasChangedRef = React.useRef<boolean>(false);
  const setHasChanged = React.useCallback(() => {
    hasChangedRef.current = true;
  }, []);

  const [inputValue, setInputValue] = React.useState<string>(
    passedInputValue ?? ""
  );

  React.useImperativeHandle(setInputValueRef, () => {
    return setInputValue;
  }, [setInputValue]);

  React.useEffect(() => {
    if (hasChangedRef.current) return;
    setInputValue(passedInputValue);
  }, [passedInputValue]);

  return { inputValue, setInputValue, setHasChanged };
}

interface GoogleMapSuggestion {
  label: string;
  getPlace: () => Promise<PlaceWithAddressComponentsAndLocation>;
}

function useGoogleMapsAutocomplete(inputValue: string) {
  const [suggestions, setSuggestions] = React.useState<GoogleMapSuggestion[]>(
    []
  );
  const { isLoaded } = useGoogleMapsApi();
  const sessionToken = React.useMemo(
    () => (isLoaded ? new google.maps.places.AutocompleteSessionToken() : null),
    [isLoaded]
  );

  const throttledGetPlacePredictions = React.useMemo(
    () =>
      throttle(async (request: google.maps.places.AutocompleteRequest) => {
        const result =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            request
          );

        if (result) {
          const suggestions = result.suggestions.reduce<GoogleMapSuggestion[]>(
            (acc, suggestion) => {
              if (suggestion.placePrediction) {
                return [
                  ...acc,
                  {
                    label: suggestion.placePrediction.text.text,
                    getPlace: async () => {
                      assert(
                        suggestion.placePrediction,
                        "PlacePrediction is null"
                      );
                      const { place } = (await suggestion.placePrediction
                        .toPlace()
                        .fetchFields({
                          fields: ["addressComponents", "location"],
                        })) as { place: PlaceWithAddressComponentsAndLocation };
                      assert(
                        "addressComponents" in place,
                        "No addressComponents in place"
                      );
                      assert("location" in place, "No location in place");

                      return place;
                    },
                  },
                ];
              }
              return acc;
            },
            []
          );
          setSuggestions(suggestions);
        }
      }, 200),
    []
  );

  React.useEffect(() => {
    if (inputValue === "") {
      setSuggestions([]);
      return;
    }

    if (!isLoaded || !sessionToken) return;

    throttledGetPlacePredictions({
      input: inputValue,
      // includedRegionCodes: ["de", "at", "ch", "be", "nl", "fr", "pl"],
      // region: "de",
      locationBias: new google.maps.LatLng(51.1657, 10.4515), // Center of Germany
      includedPrimaryTypes: [
        "street_address",
        "route",
        // "political",
        "locality",
        "sublocality",
        "postal_code",
        // "neighborhood",
      ],
      sessionToken,
    });

    return () => {
      throttledGetPlacePredictions.cancel();
    };
  }, [inputValue, throttledGetPlacePredictions, sessionToken, isLoaded]);

  return { suggestions };
}

// The address components that name the city, most specific first.
const CITY_COMPONENT_TYPES = [
  "locality",
  "postal_town",
  "sublocality_level_1",
  "sublocality",
  "administrative_area_level_3",
];

// Countries that write the house number before the street ("10 Rue de
// Rivoli"); everywhere else it follows the street ("Kurfürstendamm 15").
const HOUSE_NUMBER_FIRST_COUNTRIES = new Set([
  "AU",
  "CA",
  "FR",
  "GB",
  "IE",
  "LU",
  "NZ",
  "US",
]);

type PlaceWithAddressComponents = google.maps.places.Place & {
  addressComponents: google.maps.places.AddressComponent[];
};
export function getAddressInfoFromPlace(
  place: PlaceWithAddressComponents,
  short: boolean = false
) {
  const addressComponentsMap = place.addressComponents.reduce<
    Record<string, google.maps.places.AddressComponent>
  >((acc, component) => {
    return {
      ...acc,
      ...component.types.reduce((a, type) => ({ ...a, [type]: component }), {}),
    };
  }, {});

  const route = getAddressComponentValue(addressComponentsMap, "route", short);
  const streetNumber = getAddressComponentValue(
    addressComponentsMap,
    "street_number",
    short
  );
  const postalCode = getAddressComponentValue(
    addressComponentsMap,
    "postal_code",
    short
  );
  // Not every country has a `locality`: the UK names the town as the postal
  // town, some places only carry a district (sublocality) or the
  // municipality (administrative area level 3).
  const city =
    CITY_COMPONENT_TYPES.map(type =>
      getAddressComponentValue(addressComponentsMap, type, short)
    ).find(value => Boolean(value)) ?? "";
  const countryCode = getAddressComponentValue(
    addressComponentsMap,
    "country",
    true
  );

  const streetParts = HOUSE_NUMBER_FIRST_COUNTRIES.has(countryCode ?? "")
    ? [streetNumber, route]
    : [route, streetNumber];

  const address = {
    city,
    countryCode,
    postalCode,
    streetName: route,
    streetNumber,
    streetLines1: streetParts.join(" ").trim(),
  };

  return address;
}

const getAddressComponentValue = (
  addressComponentsMap: Record<string, google.maps.places.AddressComponent>,
  key: string,
  short?: boolean
) => {
  const component = addressComponentsMap[key];

  if (component) {
    return short ? component.shortText : component.longText;
  } else {
    return "";
  }
};

type PlaceWithLocation = google.maps.places.Place & {
  location: google.maps.LatLng;
};
export function getLocationFromPlace(place: PlaceWithLocation) {
  return place.location
    ? { lat: place.location.lat(), lng: place.location.lng() }
    : null;
}
